import type { $Enums } from '@prisma/client';
import { getStatusLabel } from '../../../status-labels';
import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, deny } from '../types';

export type ProductionRunStatus = $Enums.ProductionRunStatus;

export type ProductionRunCountryOfOrigin = $Enums.CountriesOfOrigin;

/** What `start` needs beyond a DRAFT status — the transitions manager's completeness checks, in its order. */
export type ProductionRunStartReadiness = {
  hasCommodity: boolean;
  hasOrganicStatus: boolean;
  /** The run's place carries the WAREHOUSE role. */
  hasWarehouse: boolean;
  hasInput: boolean;
  hasOutput: boolean;
  /** `<lot> (<country>)` for every component lot whose product country the run's country refuses. */
  countryOfOriginMismatches: readonly string[];
};

/** What `complete` needs beyond an IN_PROGRESS status — the completion guards, in their order. */
export type ProductionRunCompleteReadiness = {
  hasZeroQuantityComponent: boolean;
  hasOutputWithoutWeight: boolean;
  /** An input lot outside OPEN (or MID_RECEIVING, when the org ships mid-receiving). */
  hasUnavailableInputLot: boolean;
};

export type ProductionRunFacts = {
  status: ProductionRunStatus;
  isTemplate: boolean;
  /** Live components on the run; on a non-template run they tie the commodity attributes to lots. */
  hasComponents: boolean;
  /** The organization's default production run template is sourced from this run. */
  isDefaultTemplate: boolean;
  countryOfOrigin: ProductionRunCountryOfOrigin | null;
  /** Absent = not judged here (the caller has not read the components); the server decides. */
  startReadiness?: ProductionRunStartReadiness;
  /** Absent = not judged here (the caller has not read the components); the server decides. */
  completeReadiness?: ProductionRunCompleteReadiness;
  /** Slugs of component lots that were soft-deleted under the run; complete and reopen would move their quantities. */
  deletedComponentLots?: readonly string[];
};

/**
 * The header fields a production run surface can write. `customFields` is the header's whole-JSON
 * write; the per-key `customField` part is ungoverned (editable in every status on both sides).
 */
export const PRODUCTION_RUN_FIELDS = [
  'commodity',
  'countryOfOrigin',
  'organic',
  'warehouse',
  'startedAt',
  'completedAt',
  'customFields',
  'label',
] as const;

export type ProductionRunField = (typeof PRODUCTION_RUN_FIELDS)[number];

/** The lifecycle operations — the state machine's events, which read as plain words already. */
export const PRODUCTION_RUN_OPS = ['start', 'complete', 'reopen'] as const;

export type ProductionRunOp = (typeof PRODUCTION_RUN_OPS)[number];

const COMPLETED = "Completed production runs can't be edited.";

/**
 * A run with a SPECIFIC country of origin may only hold lots of that country; MIXED (or no
 * country) lifts it, and a product without a country is treated as compatible.
 */
export const productionRunCountryOfOriginMismatch = (
  runCountryOfOrigin: ProductionRunCountryOfOrigin | null | undefined,
  productCountryOfOrigin: ProductionRunCountryOfOrigin | null | undefined
): boolean => {
  if (!runCountryOfOrigin || runCountryOfOrigin === 'MIXED') return false;
  if (productCountryOfOrigin == null) return false;
  return productCountryOfOrigin !== runCountryOfOrigin;
};

/** One component as both sides read it for the start and complete readiness facts. */
export type ProductionRunReadinessComponent = {
  type: 'INPUT' | 'OUTPUT';
  lotId?: number | null;
  quantityChange?: number | null;
  /** Any non-null value counts as set (the api reads a Prisma Decimal, the web a number). */
  weightProduced?: unknown;
  lot?: {
    slug?: string | null;
    status?: string | null;
    /** Any non-null value means deleted (the api reads a Date, the web an ISO string). */
    deletedAt?: unknown;
    product?: { countryOfOrigin?: ProductionRunCountryOfOrigin | null } | null;
  } | null;
};

export const productionRunStartReadiness = ({
  commodityId,
  organic,
  hasWarehouse,
  countryOfOrigin,
  components,
}: {
  commodityId: number | null | undefined;
  organic: boolean | null | undefined;
  hasWarehouse: boolean;
  countryOfOrigin: ProductionRunCountryOfOrigin | null | undefined;
  components: readonly ProductionRunReadinessComponent[];
}): ProductionRunStartReadiness => ({
  hasCommodity: Boolean(commodityId),
  hasOrganicStatus: organic != null,
  hasWarehouse,
  hasInput: components.some((component) => component.type === 'INPUT'),
  hasOutput: components.some((component) => component.type === 'OUTPUT'),
  countryOfOriginMismatches: components
    .filter((component) =>
      productionRunCountryOfOriginMismatch(countryOfOrigin, component.lot?.product?.countryOfOrigin)
    )
    .map((component) => `${component.lot?.slug ?? component.lotId} (${component.lot?.product?.countryOfOrigin})`),
});

/** The input lot statuses completion accepts; MID_RECEIVING joins them behind the org's flag. */
export const productionRunCompletableInputLotStatuses = (allowMidReceivingShipping: boolean): readonly string[] =>
  allowMidReceivingShipping ? ['OPEN', 'MID_RECEIVING'] : ['OPEN'];

export const productionRunCompleteReadiness = ({
  components,
  completableInputLotStatuses,
}: {
  components: readonly ProductionRunReadinessComponent[];
  /** Absent = the caller does not read lot statuses; the lot check is left to the server. */
  completableInputLotStatuses?: readonly string[];
}): ProductionRunCompleteReadiness => ({
  hasZeroQuantityComponent: components.some((component) => component.quantityChange === 0),
  hasOutputWithoutWeight: components.some(
    (component) => component.type === 'OUTPUT' && component.weightProduced == null
  ),
  hasUnavailableInputLot:
    completableInputLotStatuses !== undefined &&
    components.some(
      (component) =>
        component.type === 'INPUT' &&
        component.lotId != null &&
        component.lot != null &&
        !completableInputLotStatuses.includes(component.lot.status ?? '')
    ),
});

export const productionRunDeletedComponentLots = (components: readonly ProductionRunReadinessComponent[]): string[] =>
  components
    .filter((component) => component.lot != null && component.lot.deletedAt != null)
    .map((component) => String(component.lot?.slug ?? component.lotId));

/**
 * THE STATE MACHINE IS THE AUTHORITY ON LIFECYCLE AVAILABILITY
 * (api/src/services/productionRuns/productionRun.states.ts):
 *
 *   draft        { start }
 *   in progress  { complete }
 *   complete     { reopen }
 */
const ACCEPTED_OPS: Record<ProductionRunStatus, readonly ProductionRunOp[]> = {
  DRAFT: ['start'],
  IN_PROGRESS: ['complete'],
  COMPLETE: ['reopen'],
};

const ACTION_LABELS = new Map<string, string>([
  ['start', 'Start'],
  ['complete', 'Complete'],
  ['reopen', 'Reopen'],
]);

export const productionRunInvalidEvent = (op: string, status: ProductionRunStatus) =>
  deny(
    'PRODUCTION_RUN_INVALID_EVENT',
    `${ACTION_LABELS.get(op) ?? op} isn't available while the production run is ${getStatusLabel(status)}.`
  );

const decideStart = (readiness: ProductionRunStartReadiness): Decision => {
  if (!readiness.hasCommodity) {
    return deny('PRODUCTION_RUN_START_COMMODITY_REQUIRED', 'Add a commodity to start.');
  }
  if (!readiness.hasOrganicStatus) {
    return deny('PRODUCTION_RUN_START_ORGANIC_REQUIRED', 'Add an organic status to start.');
  }
  if (!readiness.hasWarehouse) {
    return deny('PRODUCTION_RUN_START_WAREHOUSE_REQUIRED', 'Add a warehouse to start.');
  }
  if (!readiness.hasInput) {
    return deny('PRODUCTION_RUN_START_INPUT_REQUIRED', 'Add an input to start.');
  }
  if (!readiness.hasOutput) {
    return deny('PRODUCTION_RUN_START_OUTPUT_REQUIRED', 'Add an output to start.');
  }
  if (readiness.countryOfOriginMismatches.length) {
    return deny('PRODUCTION_RUN_START_COUNTRY_OF_ORIGIN_MISMATCH', 'Input lots have a different country of origin.');
  }
  return allow();
};

const decideComplete = (readiness: ProductionRunCompleteReadiness): Decision => {
  if (readiness.hasZeroQuantityComponent) {
    return deny('PRODUCTION_RUN_COMPLETE_ZERO_QUANTITY', 'Enter a quantity on every input and output to complete.');
  }
  if (readiness.hasOutputWithoutWeight) {
    return deny('PRODUCTION_RUN_COMPLETE_OUTPUT_WEIGHT_REQUIRED', 'Enter weight produced on every output.');
  }
  if (readiness.hasUnavailableInputLot) {
    return deny('PRODUCTION_RUN_COMPLETE_INPUT_LOTS_NOT_OPEN', 'Every input lot must be Open to complete.');
  }
  return allow();
};

const deletedComponentLotDenial = (lots: readonly string[], op: ProductionRunOp): Decision => {
  const names = lots.map((lot) => `#${lot}`).join(', ');
  return deny(
    'PRODUCTION_RUN_COMPONENT_LOT_DELETED',
    lots.length === 1
      ? `Lot ${names} was deleted. Remove it from this run to ${op}.`
      : `Lots ${names} were deleted. Remove them from this run to ${op}.`
  );
};

const decideOperation = (facts: ProductionRunFacts, op: ProductionRunOp): Decision => {
  if (!ACCEPTED_OPS[facts.status].includes(op)) {
    return productionRunInvalidEvent(op, facts.status);
  }
  if ((op === 'complete' || op === 'reopen') && facts.deletedComponentLots?.length) {
    return deletedComponentLotDenial(facts.deletedComponentLots, op);
  }
  if (op === 'start' && facts.startReadiness) return decideStart(facts.startReadiness);
  if (op === 'complete' && facts.completeReadiness) return decideComplete(facts.completeReadiness);
  return allow();
};

type ProductionRunAction = Action<ProductionRunField, ProductionRunOp>;

const decideEntity = (facts: ProductionRunFacts, action: ProductionRunAction): Decision => {
  switch (action.type) {
    case 'view':
    case 'add':
      return allow();
    case 'edit':
      // A header write with no field (an empty patch) is still a write to a completed run.
      return action.field === undefined && facts.status === 'COMPLETE'
        ? deny('PRODUCTION_RUN_FIELD_LOCKED', COMPLETED)
        : allow();
    case 'delete':
      if (facts.isDefaultTemplate) {
        return deny('PRODUCTION_RUN_DELETE_DEFAULT_TEMPLATE', "The default template can't be deleted.");
      }
      return facts.status === 'DRAFT'
        ? allow()
        : deny('PRODUCTION_RUN_DELETE_LOCKED', 'Only Draft production runs can be deleted.');
    case 'perform':
      return decideOperation(facts, action.op);
  }
};

/** Every header field locks once the run is complete. */
const headerRule = (facts: ProductionRunFacts, action: ProductionRunAction): Decision =>
  action.type !== 'edit' || facts.status !== 'COMPLETE' ? allow() : deny('PRODUCTION_RUN_FIELD_LOCKED', COMPLETED);

/** The commodity attributes also lock in progress, and on a non-template run once it has components. */
const commodityAttributeRule = (facts: ProductionRunFacts, action: ProductionRunAction): Decision => {
  const header = headerRule(facts, action);
  if (!header.allowed || action.type !== 'edit') return header;

  if (facts.status === 'IN_PROGRESS') {
    return deny('PRODUCTION_RUN_COMMODITY_IN_PROGRESS', 'Commodity is locked once the run is In Progress.');
  }
  // Template outputs are lot-less, so a template's attributes have no lot tie and stay editable.
  if (!facts.isTemplate && facts.hasComponents) {
    return deny('PRODUCTION_RUN_COMMODITY_HAS_COMPONENTS', 'Remove inputs and outputs to change the commodity.');
  }
  return allow();
};

/** The label (slug) is editable only in DRAFT. */
const labelRule = (facts: ProductionRunFacts, action: ProductionRunAction): Decision =>
  action.type !== 'edit' || facts.status === 'DRAFT'
    ? allow()
    : deny('PRODUCTION_RUN_LABEL_LOCKED', 'The label can only be changed in Draft.');

const fieldRules = {
  commodity: commodityAttributeRule,
  countryOfOrigin: commodityAttributeRule,
  organic: commodityAttributeRule,
  warehouse: headerRule,
  startedAt: headerRule,
  completedAt: headerRule,
  customFields: headerRule,
  label: labelRule,
} satisfies Record<ProductionRunField, typeof headerRule>;

export const productionRunPolicy: Policy<ProductionRunFacts, ProductionRunField, ProductionRunOp> = definePolicy<
  ProductionRunFacts,
  ProductionRunField,
  ProductionRunOp
>({
  kind: 'production run',
  fields: PRODUCTION_RUN_FIELDS,
  ops: PRODUCTION_RUN_OPS,
  requires: {},
  entity: decideEntity,
  field: fieldRules,
});
